import React from 'react'
import styled from 'styled-components'

import { roundWithLocale } from 'utils/math'
import { Preset, Rect, Layer, SelectionType } from 'modules/shares/types'
import { InspectorData } from 'modules/shares/Inspector/types'
import ExportableIcon from 'modules/shares/Inspector/ExportableIcon'
import Section from '../Section'
import Attribute from '../Attribute'
import { isSymbolLayer } from '../../../Symbol'

import canvasDotted from 'icons/canvas-dotted-18.svg'
import artboardIconURL from './artboard.svg'
import textLayerIconURL from 'icons/inspector-text-layer.svg'
import shapeLayerIconURL from 'icons/inspector-shape-layer.svg'
import bitmapLayerIconURL from 'icons/inspector-bitmap-layer.svg'
import layerGroupIconURL from 'icons/inspector-layer-group.svg'
import sliceIconURL from 'icons/inspector-slice.svg'
import symbolIconURL from 'icons/inspector-symbol-17.svg'

/**
 * TYPES
 */
interface IconProps {
  selection: SelectionType
  className?: string
  hasExports: boolean
}

/**
 * HELPERS
 */
const pageOrArtboard = (inspectorData?: InspectorData): 'page' | 'artboard' =>
  inspectorData?.frame?.width && inspectorData?.frame?.height
    ? 'artboard'
    : 'page'

const imagePropsForSelection = (selectionType: SelectionType) => {
  switch (selectionType) {
    case 'text':
      return {
        src: textLayerIconURL,
        alt: 'Text layer icon',
        width: '17px',
        height: '17px',
      }

    case 'polygon':
    case 'triangle':
    case 'star':
    case 'rectangle':
    case 'oval':
    case 'shapePath':
    case 'shapeGroup':
      return {
        src: shapeLayerIconURL,
        alt: 'Shape layer icon',
        width: '17px',
        height: '17px',
      }

    case 'bitmap':
      return {
        src: bitmapLayerIconURL,
        alt: 'Bitmap layer icon',
        width: '17px',
        height: '17px',
      }

    case 'group':
      return {
        src: layerGroupIconURL,
        alt: 'Layer group icon',
        width: '17px',
        height: '17px',
      }

    case 'artboard':
      return {
        src: artboardIconURL,
        alt: 'Artboard icon',
        width: '20px',
        height: '18px',
      }

    case 'slice':
      return {
        src: sliceIconURL,
        alt: 'Slice icon',
        width: '17px',
        height: '17px',
      }

    case 'symbol':
      return {
        src: symbolIconURL,
        alt: 'Symbol icon',
        width: '17px',
        height: '17px',
      }

    default:
    case 'page':
      return {
        src: canvasDotted,
        alt: 'Page icon',
        width: '18px',
        height: '18px',
      }
  }
}

// Some icons need an extra space for the exportable icon (the knife icon)
const isNeededExtraSpaceForExportableIcon = (
  selection: SelectionType
): boolean =>
  selection === 'artboard' || selection === 'text' || selection === 'group'

/**
 * STYLES
 */
const ExportableIconWrapper = styled.div<{ selection: SelectionType }>`
  width: 21px;
  height: 21px;
  position: absolute;
  transform: ${({ selection }) =>
    isNeededExtraSpaceForExportableIcon(selection)
      ? 'translate(2px, 1px)'
      : undefined};
`

const BaseIcon = ({ selection, hasExports, className }: IconProps) => (
  <div className={className}>
    {/* Slice already has an exportable icon present on the icon */}
    {hasExports && selection !== 'slice' && (
      <ExportableIconWrapper selection={selection}>
        <ExportableIcon maskColor="#F0F0F0" />
      </ExportableIconWrapper>
    )}
    {/* eslint-disable-next-line jsx-a11y/alt-text */}
    <img {...imagePropsForSelection(selection)} />
  </div>
)

export const Icon = styled(BaseIcon)`
  position: relative;

  display: flex;
  justify-content: center;
  align-items: center;

  width: 36px;
  height: 36px;

  background-color: #f0f0f0;
  border-radius: 4px;
  margin-right: 12px;
`

const Name = styled.div`
  color: ${({ theme }) => theme.colors.text.dark.primary};
  font-size: 0.8125rem;
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  letter-spacing: -0.07px;
`

const Size = styled.div`
  font-size: 0.6875rem;
  color: ${({ theme }) => theme.colors.text.dark.tertiary};
  letter-spacing: 0.07px;
`

const isPresetResized = ({
  frame,
  preset,
}: {
  frame: Rect
  preset?: Preset
}) => {
  if (!preset) {
    return false
  }

  return frame.width !== preset.width || frame.height !== preset.height
}

/**
 * HELPER COMPONENTS
 */
const ArtboardOverview: React.FC<{ inspectorData: InspectorData }> = ({
  inspectorData,
}) => {
  const preset = inspectorData.preset
  const name = preset?.name || 'Custom'
  const frame = inspectorData.frame
  const isResized = isPresetResized({ frame, preset })

  return (
    <>
      <Name>{name}</Name>
      {preset && (
        <Size>
          {roundWithLocale(frame.width, 2)} x {roundWithLocale(frame.height, 2)}{' '}
          {isResized && '(Resized)'}
        </Size>
      )}
    </>
  )
}

const LayerOverview: React.FC<{ layer: Layer }> = ({ layer }) => (
  <Name>{layer.name}</Name>
)

/**
 * MAIN COMPONENT
 * Overview info, used for both layer and artboard/page info
 */
const Overview: React.FC<{ inspectorData?: InspectorData; layer?: Layer }> = ({
  inspectorData,
  layer,
}) => {
  if (!inspectorData && !layer) {
    return null
  }

  let hasExports = false
  if (layer) {
    hasExports = !!layer?.exportFormats?.length
  } else {
    hasExports = !!inspectorData?.exportFormats?.length
  }

  const layerType = isSymbolLayer(layer)
    ? 'symbol'
    : layer?.type || pageOrArtboard(inspectorData)

  return (
    <Section data-testid="inspector-sidebar-overview">
      <Attribute>
        <Icon selection={layerType} hasExports={hasExports} />
        <div>
          {layer ? (
            <LayerOverview layer={layer} />
          ) : (
            <ArtboardOverview inspectorData={inspectorData!} />
          )}
        </div>
      </Attribute>
    </Section>
  )
}

export default Overview
